import "./holiday.css";
import { useEffect, useState } from "react";
import { InputNumber, Modal } from "antd";
import dayjs from "dayjs";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Spinner from "@src/components/spinner";
import { InfoHint } from "@src/components/infoHint/infoHint";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getHolidayRatesNeededQuery } from "@src/util/query/holiday.query";
import { setHolidayPayRatesMutation } from "@src/util/mutation/holiday.mutation";
import { requestDates } from "./holidayRequestActions";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Holidays that need an hourly pay rate before payroll: the employee has no pay rate of their
 * own, and pay tables are per site and duty type, which a holiday doesn't have. Each one
 * suggests the rate from the last duty they worked that month; the manager accepts it, types
 * another, or leaves it for later. Used from the dashboard notice (everyone) and an employee's
 * Calendar tab (just them).
 */
export const HolidayRatesModal = ({ visible, close, employeeId, onSaved }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { data, loading, refetch } = useQuery(getHolidayRatesNeededQuery, {
        variables: { employeeId: employeeId || null }, skip: !visible, fetchPolicy: "network-only",
    });
    const result = data?.getHolidayRatesNeeded?.response;
    const items = result?.items || [];
    const [rates, setRates] = useState({});
    useEffect(() => { if (visible) setRates({}); }, [visible]);
    const [saveRates, { loading: saving }] = useMutation(setHolidayPayRatesMutation);

    const fillSuggested = () => setRates(Object.fromEntries(items.filter((i) => i.suggestion?.rate).map((i) => [i.requestId, i.suggestion.rate])));
    const filled = items.filter((i) => Number(rates[i.requestId]) > 0);

    const save = async () => {
        try {
            await saveRates({
                variables: {
                    input: filled.map((i) => {
                        const rate = Number(rates[i.requestId]);
                        const suggested = i.suggestion && round2(i.suggestion.rate) === round2(rate);
                        return {
                            requestId: i.requestId,
                            rate,
                            currency: i.suggestion?.currency || result?.defaultCurrency || null,
                            note: suggested ? `Rate at ${i.suggestion.siteName}${i.suggestion.dutyType ? ` (${i.suggestion.dutyType})` : ""}, last worked ${dayjs(i.suggestion.date).format("DD/MM/YYYY")}` : "Set by a manager",
                        };
                    }),
                },
            });
            dispatch(setNotification({ error: false, notificationState: true, message: `Holiday pay rate saved for ${filled.length} leave request${filled.length === 1 ? "" : "s"}` }));
            await refetch();
            setRates({});
            onSaved?.();
            if (filled.length === items.length) close();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
        }
    };

    return (
        <Modal open={visible} width={760} closable={false} onCancel={close} zIndex={1100}
            footer={
                <div className="drawer-footer holiday-action-footer">
                    <button className={saving || !filled.length ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={saving || !filled.length} onClick={save}>
                        {saving ? <Spinner /> : `Save ${filled.length || ""} Rate${filled.length === 1 ? "" : "s"}`}
                    </button>
                    <button className="drawer-outlined-btn" onClick={close} disabled={saving}>Remind Me Later</button>
                </div>
            }
        >
            <div className="modal-header-title">
                <span>
                    Holiday Pay Rates Required
                    <InfoHint title="Why a rate is needed" lines={[
                        "Holiday is paid at the employee's own pay rate (on their profile) when they have one - the same first step Calculate Pay & Bill uses.",
                        "These employees don't have one: they're paid by site / duty type pay tables, and a holiday has neither. So a manager sets the hourly rate for each holiday.",
                        "The suggestion is what they were paid an hour on the last duty they worked that month (or the 90 days before).",
                        "Until a rate is set, the pay reports show these days as \"Holiday Pay (Rate Required)\" with no pay.",
                    ]} />
                </span>
                <span onClick={close}><FontAwesomeIcon className="close" icon={faClose} /></span>
            </div>
            <div className="modal-body holiday-action-body">
                {loading && !result ? <div className="holiday-action-loading"><Spinner /></div> : !items.length ? (
                    <div className="holiday-action-line">All approved leave {result?.until ? `up to ${dayjs(result.until).format("DD/MM/YYYY")} ` : ""}has a holiday pay rate.</div>
                ) : (
                    <>
                        <div className="holiday-action-line" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span>{items.length} approved leave request{items.length === 1 ? "" : "s"} up to {dayjs(result.until).format("DD/MM/YYYY")} require{items.length === 1 ? "s" : ""} an hourly holiday pay rate.</span>
                            {items.some((i) => i.suggestion) && <button className="holiday-link-btn" onClick={fillSuggested}>Apply Suggested Rates</button>}
                        </div>
                        {items.map((item) => (
                            <div key={item.requestId} className="holiday-rate-row">
                                <div>
                                    {!employeeId ? (
                                        <button className="holiday-link-btn" onClick={() => { close(); navigate(`/user/employee-detail/${item.employeeId}/tab/calendar`); }}>{item.employeeName}</button>
                                    ) : <b>{item.employeeName}</b>}
                                    <div>{item.typeLabel} · {requestDates(item)} · {round2(item.hours)} hours</div>
                                    <div className="holiday-field-hint" style={{ marginTop: 2 }}>
                                        {item.suggestion ? `${item.suggestion.text}.` : "No completed duty with calculated pay in that month or the previous 90 days. Please enter a rate."}
                                    </div>
                                </div>
                                <div className="holiday-rate-input">
                                    <InputNumber
                                        min={0}
                                        step={0.25}
                                        value={rates[item.requestId] ?? null}
                                        onChange={(value) => setRates((prev) => ({ ...prev, [item.requestId]: value }))}
                                        placeholder={item.suggestion ? String(item.suggestion.rate) : "Rate"}
                                        addonAfter={`${item.suggestion?.currency || result?.defaultCurrency || "GBP"}/h`}
                                        style={{ width: 170 }}
                                    />
                                    {Number(rates[item.requestId]) > 0 && (
                                        <div className="holiday-field-hint" style={{ marginTop: 2 }}>= {round2(item.hours * Number(rates[item.requestId]))} pay</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </Modal>
    );
};
